// src/pages/TagPage.js
import React, { useState, useEffect, useRef } from 'react';
import Layout from './Layout';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../firebase';
import { useParams } from 'react-router-dom';
import SearchVerseSystem from '../components/searchVerseSystem';

const TagPage = () => {
  const { tagName } = useParams();
  const [verses, setVerses] = useState([]);
  const [filteredVerses, setFilteredVerses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const versesPerPage = 20;
  const isInitialLoad = useRef(true);

  useEffect(() => {
    const fetchVersesByTag = async () => {
      setLoading(true);
      try {
        const q = query(collection(db, 'verses'), where('tags', 'array-contains', tagName));
        const querySnapshot = await getDocs(q);
        const versesList = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setVerses(versesList);
        setFilteredVerses(versesList);
      } catch (error) {
        console.error('Error fetching verses by tag:', error);
      } finally {
        setLoading(false);
        isInitialLoad.current = false;
      }
    };

    fetchVersesByTag();
  }, [tagName]);

  const handleSearch = (searchTerm) => {
    const lowercasedTerm = searchTerm.toLowerCase();
    const filtered = verses.filter(verse =>
      (verse.tags && verse.tags.some(t => t.toLowerCase().includes(lowercasedTerm))) ||
      (verse.categories && verse.categories.some(c => c.toLowerCase().includes(lowercasedTerm))) ||
      verse.text.toLowerCase().includes(lowercasedTerm) ||
      verse.reference.toLowerCase().includes(lowercasedTerm)
    );
    setFilteredVerses(filtered);
    setCurrentPage(1); // Reset to the first page on new search
  };

  const handleClearFilters = () => {
    if (!isInitialLoad.current && verses.length > 0) {
      setFilteredVerses(verses);
      setCurrentPage(1);
      window.scrollTo(0, 0); // Ensure view is reset to the top
    }
  };

  // Pagination Logic
  const indexOfLastVerse = currentPage * versesPerPage;
  const indexOfFirstVerse = indexOfLastVerse - versesPerPage;
  const currentVerses = filteredVerses.slice(indexOfFirstVerse, indexOfLastVerse);
  const totalPages = Math.ceil(filteredVerses.length / versesPerPage);

  const goToNextPage = () => {
    if (currentPage < totalPages) setCurrentPage(currentPage + 1);
  };

  const goToPreviousPage = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1);
  };

  if (loading || (isInitialLoad.current && verses.length === 0)) {
    return (
      <Layout>
        <p className="loading-message">Loading verses...</p>
      </Layout>
    );
  }

  return (
    <div>
      <Layout>
        <div className="page-container">
          <h2 className="page-heading">Verses for Tag: {tagName}</h2>

          <SearchVerseSystem onSearch={handleSearch} onClearFilters={handleClearFilters} />

          {currentVerses.length === 0 ? (
            <p className="no-verses-message">No verses found for this tag.</p>
          ) : (
            <ul className="verses-list">
              {currentVerses.map((verse) => (
                <li key={verse.id} className="verse-item">
                  <h3 className="verse-reference">{verse.reference}</h3>
                  <p className="verse-text">{verse.text}</p>
                  {verse.question && (
                    <p className="verse-question">
                      <strong>Reflection Question:</strong> {verse.question}
                    </p>
                  )}
                  {verse.categories && verse.categories.length > 0 && (
                    <p className="verse-categories">
                      <strong>Categories:</strong> {verse.categories.map((category, index) => (
                        <span key={index}>
                          {category}
                          {index < verse.categories.length - 1 ? ', ' : ''}
                        </span>
                      ))}
                    </p>
                  )}
                  {verse.tags && verse.tags.length > 0 && (
                    <p className="verse-tags">
                      <strong>Tags:</strong> {verse.tags.map((tag, index) => (
                        <span key={index}>
                          {tag}
                          {index < verse.tags.length - 1 ? ', ' : ''}
                        </span>
                      ))}
                    </p>
                  )}
                  {verse.url && (
                    <p className="verse-url">
                      <a href={verse.url} target="_blank" rel="noopener noreferrer">View Verse on Bible.com</a>
                    </p>
                  )}
                </li>
              ))}
            </ul>
          )}

          {/* Pagination Controls */}
          <div className="flex items-center justify-center mt-8 space-x-4">
            <button
              onClick={goToPreviousPage}
              disabled={currentPage === 1}
              className={`px-4 py-2 rounded-lg font-semibold ${
                currentPage === 1
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-blue-500 text-white hover:bg-blue-600'
              }`}
            >
              &laquo; Previous
            </button>

            <span className="text-lg font-medium text-gray-700">
              Page {currentPage} of {totalPages}
            </span>

            <button
              onClick={goToNextPage}
              disabled={currentPage === totalPages}
              className={`px-4 py-2 rounded-lg font-semibold ${
                currentPage === totalPages
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-blue-500 text-white hover:bg-blue-600'
              }`}
            >
              Next &raquo;
            </button>
          </div>
        </div>
      </Layout>
    </div>
  );
};

export default TagPage;
